<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useDocumentViewer } from '@/composables/useDocumentViewer'
import { useDocumentsStore } from '@/views/Documents/store'
import { useUsersStore } from '@/views/Users/store'
import { useAuthStore } from '@/stores/auth'
import { useMarkdownPdf } from '@/composables/useMarkdownPdf'
import { synthesizeSpeech } from '@/services/ttsService'
import { MdPreview } from 'md-editor-v3'
import 'md-editor-v3/lib/preview.css'
import type { Document, DocumentEntityType, DocumentType } from '@/types'
import { formatDate, getInitials } from '@/utils/formatters'

import CommentsList from '@/views/Comments/components/CommentsList.vue'
import DocumentReactions from '@/views/Documents/components/DocumentReactions.vue'
import DocumentMarkdownView from '@/views/Documents/components/DocumentMarkdownView.vue'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  FileText,
  Lock,
  Calendar,
  Hash,
  Download,
  Loader2,
  Eye,
  Minus,
  X,
  Search,
  Copy,
  Pencil,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Headphones,
  Play,
  Pause,
  Square,
  Volume2,
  Sparkles,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from 'lucide-vue-next'

const {
  isOpen,
  currentDocumentId,
  currentDocument,
  close,
  minimize,
  setCurrentDocument,
  requestEdit,
} = useDocumentViewer()

const documentsStore = useDocumentsStore()
const usersStore = useUsersStore()
const authStore = useAuthStore()

// Mobile detection
const isMobile = ref(window.innerWidth < 768)
const onResize = () => { isMobile.value = window.innerWidth < 768 }
onMounted(() => window.addEventListener('resize', onResize))
onUnmounted(() => window.removeEventListener('resize', onResize))

// Search state
const searchQuery = ref('')
const entityFilter = ref<string>('all')
const searchPanelCollapsed = ref(isMobile.value)
const commentsPanelCollapsed = ref(isMobile.value)
const searchResults = ref<Document[]>([])
const isSearching = ref(false)
const hasSearched = ref(false)

const entityTypeOptions: { value: string; label: string }[] = [
  { value: 'all', label: 'Todas las entidades' },
  { value: 'project', label: 'Proyectos' },
  { value: 'requirement', label: 'Requerimientos' },
  { value: 'epic', label: 'Epicas' },
  { value: 'ticket', label: 'Tickets' },
  { value: 'application', label: 'Aplicaciones' },
  { value: 'meeting', label: 'Reuniones' },
  { value: 'petition', label: 'Peticiones' },
]

const documentTypeLabels: Record<DocumentType, string> = {
  scope: 'Alcance',
  architecture: 'Arquitectura',
  specifications: 'Especificaciones',
  manual: 'Manual',
  other: 'Otro',
}

const documentTypeColors: Record<DocumentType, string> = {
  scope: 'bg-blue-500',
  architecture: 'bg-purple-500',
  specifications: 'bg-green-500',
  manual: 'bg-orange-500',
  other: 'bg-gray-500',
}

const entityTypeLabels: Record<string, string> = {
  project: 'Proyecto',
  requirement: 'Requerimiento',
  epic: 'Epica',
  ticket: 'Ticket',
  application: 'Aplicacion',
  meeting: 'Reunion',
  petition: 'Peticion',
}

// Load document if opened by ID (e.g. from URL)
watch(
  [isOpen, currentDocumentId],
  async ([open, docId]) => {
    if (open && docId && !currentDocument.value) {
      const doc = await documentsStore.fetchDocumentById(docId)
      if (doc) {
        setCurrentDocument(doc)
        documentsStore.incrementViewCount(doc.id)
      }
    }
    if (open && currentDocument.value) {
      documentsStore.incrementViewCount(currentDocument.value.id)
    }
  },
  { immediate: true },
)

// Reset search state when panel closes, collapse panels on mobile when opening
watch(isOpen, (open) => {
  if (!open) {
    hasSearched.value = false
    searchResults.value = []
    searchQuery.value = ''
    entityFilter.value = 'all'
  } else if (isMobile.value) {
    searchPanelCollapsed.value = true
    commentsPanelCollapsed.value = true
  }
})

const authorName = computed(() => {
  if (!currentDocument.value) return ''
  const user = usersStore.users.find((u) => u.id === currentDocument.value?.createdBy)
  return user?.displayName || 'Usuario desconocido'
})

const authorUser = computed(() => {
  if (!currentDocument.value) return null
  return usersStore.users.find((u) => u.id === currentDocument.value?.createdBy) || null
})

const updatedByName = computed(() => {
  if (!currentDocument.value) return ''
  const user = usersStore.users.find((u) => u.id === currentDocument.value?.updatedBy)
  return user?.displayName || 'Usuario desconocido'
})

// PDF
const pdfMarkdown = computed(() => currentDocument.value?.content || '')
const pdfFilename = computed(() => currentDocument.value?.title || 'documento')
const pdfDocId = computed(() => currentDocument.value?.id || '')
const { isGenerating: isGeneratingPdf, downloadPdf } = useMarkdownPdf(pdfMarkdown, pdfFilename, pdfDocId)

// Copilot Summary
const summaryIsGenerating = ref(false)
const summaryError = ref<string | null>(null)
const summaryCollapsed = ref(false)

const hasSummary = computed(() => !!currentDocument.value?.copilotSummary?.content)
const summaryIsStale = computed(() => currentDocument.value?.copilotSummary?.isStale ?? false)
// ~4 chars per token for Spanish; 10k tokens ≈ 40k chars
const contentTooLong = computed(() => (currentDocument.value?.content?.length ?? 0) > 40000)

const handleGenerateSummary = async () => {
  if (!currentDocument.value) return
  summaryIsGenerating.value = true
  summaryError.value = null
  try {
    await documentsStore.generateSummary(currentDocument.value.id)
    // Refresh the document to get updated copilotSummary
    const updated = await documentsStore.fetchDocumentById(currentDocument.value.id)
    if (updated) setCurrentDocument(updated)
  } catch (error) {
    summaryError.value = error instanceof Error ? error.message : 'Error al generar resumen'
  } finally {
    summaryIsGenerating.value = false
  }
}

const handleConfirmSummary = async () => {
  if (!currentDocument.value) return
  try {
    await documentsStore.confirmSummary(currentDocument.value.id)
    // Update local view
    if (currentDocument.value.copilotSummary) {
      setCurrentDocument({
        ...currentDocument.value,
        copilotSummary: {
          ...currentDocument.value.copilotSummary,
          isStale: false,
          generatedByVersion: currentDocument.value.version,
        },
      })
    }
  } catch { /* ignore */ }
}

// TTS Audio Player
const ttsAudioUrl = ref<string | null>(null)
const ttsAudio = ref<HTMLAudioElement | null>(null)
const ttsIsLoading = ref(false)
const ttsIsPlaying = ref(false)
const ttsShowPlayer = ref(false)
const ttsCurrentTime = ref(0)
const ttsDuration = ref(0)
const ttsError = ref<string | null>(null)

const ttsFormattedTime = computed(() => {
  const format = (s: number) => {
    const m = Math.floor(s / 60)
    const sec = Math.floor(s % 60)
    return `${m}:${sec.toString().padStart(2, '0')}`
  }
  return `${format(ttsCurrentTime.value)} / ${format(ttsDuration.value)}`
})

const ttsProgress = computed(() => {
  if (ttsDuration.value === 0) return 0
  return (ttsCurrentTime.value / ttsDuration.value) * 100
})

const startTts = async () => {
  if (!currentDocument.value?.content) return
  ttsShowPlayer.value = true
  ttsIsLoading.value = true
  ttsError.value = null

  // Cleanup previous
  if (ttsAudio.value) {
    ttsAudio.value.pause()
    ttsAudio.value = null
  }
  if (ttsAudioUrl.value) {
    URL.revokeObjectURL(ttsAudioUrl.value)
    ttsAudioUrl.value = null
  }

  try {
    const blob = await synthesizeSpeech(currentDocument.value.content, 'es', 'md')
    const url = URL.createObjectURL(blob)
    ttsAudioUrl.value = url

    const audio = new Audio(url)
    ttsAudio.value = audio

    audio.addEventListener('loadedmetadata', () => {
      ttsDuration.value = audio.duration
    })
    audio.addEventListener('timeupdate', () => {
      ttsCurrentTime.value = audio.currentTime
    })
    audio.addEventListener('ended', () => {
      ttsIsPlaying.value = false
      ttsCurrentTime.value = 0
    })
    audio.addEventListener('error', () => {
      ttsError.value = 'Error al reproducir audio'
      ttsIsPlaying.value = false
    })

    await audio.play()
    ttsIsPlaying.value = true
  } catch (error) {
    ttsError.value = error instanceof Error ? error.message : 'Error al generar audio'
  } finally {
    ttsIsLoading.value = false
  }
}

const togglePlayPause = () => {
  if (!ttsAudio.value) return
  if (ttsIsPlaying.value) {
    ttsAudio.value.pause()
    ttsIsPlaying.value = false
  } else {
    ttsAudio.value.play()
    ttsIsPlaying.value = true
  }
}

const stopTts = () => {
  if (ttsAudio.value) {
    ttsAudio.value.pause()
    ttsAudio.value.currentTime = 0
    ttsIsPlaying.value = false
    ttsCurrentTime.value = 0
  }
}

const closeTtsPlayer = () => {
  stopTts()
  ttsShowPlayer.value = false
  if (ttsAudioUrl.value) {
    URL.revokeObjectURL(ttsAudioUrl.value)
    ttsAudioUrl.value = null
  }
  ttsAudio.value = null
  ttsError.value = null
}

const seekTts = (event: MouseEvent) => {
  if (!ttsAudio.value || ttsDuration.value === 0) return
  const target = event.currentTarget as HTMLElement
  const rect = target.getBoundingClientRect()
  const x = event.clientX - rect.left
  const pct = x / rect.width
  ttsAudio.value.currentTime = pct * ttsDuration.value
}

// Cleanup TTS when document changes or panel closes
watch([isOpen, currentDocumentId], () => {
  closeTtsPlayer()
})

const copyDocumentId = async () => {
  if (!currentDocument.value) return
  try {
    await navigator.clipboard.writeText(currentDocument.value.id)
  } catch { /* ignore */ }
}

const doSearch = async () => {
  isSearching.value = true
  hasSearched.value = true
  try {
    const filter = entityFilter.value === 'all' ? undefined : entityFilter.value as DocumentEntityType
    searchResults.value = await documentsStore.searchDocuments(searchQuery.value || undefined, filter)
  } finally {
    isSearching.value = false
  }
}

const selectDocument = (doc: Document) => {
  setCurrentDocument(doc)
  const { open } = useDocumentViewer()
  open(doc)
}

const getDocAuthor = (userId: string): string => {
  const user = usersStore.users.find((u) => u.id === userId)
  return user?.displayName || 'Desconocido'
}

onMounted(async () => {
  if (usersStore.users.length === 0) {
    await usersStore.fetchUsers()
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      leave-active-class="transition-all duration-200 ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="isOpen" class="fixed inset-0 z-[100] bg-background flex flex-col">
        <!-- Header -->
        <div class="h-14 border-b px-6 py-3 flex items-center justify-between shrink-0">
          <div class="flex items-center gap-2">
            <FileText class="h-5 w-5" />
            <h1 class="text-lg font-semibold">Visor de Documentos</h1>
            <Badge v-if="currentDocument" variant="outline">
              {{ currentDocument.title }}
            </Badge>
          </div>
          <div class="flex items-center gap-2">
            <Button
              v-if="currentDocument"
              variant="outline"
              size="sm"
              @click="requestEdit(currentDocument!)"
            >
              <Pencil class="h-4 w-4 mr-1" />
              Editar
            </Button>
            <Button
              v-if="currentDocument && !ttsShowPlayer && authStore.isModuleEnabled('voiceNotes')"
              variant="outline"
              size="sm"
              @click="startTts"
              :disabled="ttsIsLoading"
            >
              <Headphones class="h-4 w-4 mr-1" />
              Escuchar
            </Button>
            <Button
              v-if="currentDocument"
              variant="outline"
              size="sm"
              @click="downloadPdf"
              :disabled="isGeneratingPdf"
            >
              <Loader2 v-if="isGeneratingPdf" class="h-4 w-4 mr-1 animate-spin" />
              <Download v-else class="h-4 w-4 mr-1" />
              PDF
            </Button>
            <Button variant="ghost" size="icon" title="Minimizar" @click="minimize">
              <Minus class="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" title="Cerrar" @click="close">
              <X class="h-4 w-4" />
            </Button>
          </div>
        </div>

        <!-- Layout -->
        <div class="flex-1 min-h-0 flex">
          <!-- Left: Search & Results (hidden on mobile) -->
          <div
            v-if="!isMobile"
            class="shrink-0 border-r flex flex-col transition-all duration-200"
            :class="searchPanelCollapsed ? 'w-10' : 'w-[320px]'"
          >
            <!-- Collapsed state -->
            <div v-if="searchPanelCollapsed" class="flex flex-col items-center py-2">
              <Button variant="ghost" size="icon" class="h-8 w-8" @click="searchPanelCollapsed = false" title="Expandir búsqueda">
                <PanelLeftOpen class="h-4 w-4" />
              </Button>
            </div>
            <!-- Search Bar (expanded) -->
            <div v-if="!searchPanelCollapsed" class="p-3 border-b space-y-2">
              <div class="relative">
                <Search class="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  v-model="searchQuery"
                  placeholder="Buscar documentos..."
                  class="pl-8 h-8 text-sm"
                  @keydown.enter="doSearch"
                />
              </div>
              <Select v-model="entityFilter">
                <SelectTrigger class="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent class="z-[200]">
                  <SelectItem
                    v-for="opt in entityTypeOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <div class="flex gap-2">
                <Button size="sm" class="flex-1 h-8" @click="doSearch" :disabled="isSearching">
                  <Loader2 v-if="isSearching" class="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  <Search v-else class="h-3.5 w-3.5 mr-1.5" />
                  Buscar
                </Button>
                <Button variant="ghost" size="icon" class="h-8 w-8 shrink-0" @click="searchPanelCollapsed = true" title="Contraer búsqueda">
                  <PanelLeftClose class="h-4 w-4" />
                </Button>
              </div>
            </div>

            <!-- Results -->
            <div v-if="!searchPanelCollapsed" class="flex-1 overflow-auto">
              <div v-if="isSearching" class="flex items-center justify-center py-8">
                <Loader2 class="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
              <div v-else-if="!hasSearched" class="py-8 text-center px-4">
                <Search class="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p class="text-sm text-muted-foreground">Usa los filtros y presiona Buscar</p>
              </div>
              <div v-else-if="searchResults.length === 0" class="py-8 text-center">
                <FileText class="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p class="text-sm text-muted-foreground">Sin resultados</p>
              </div>
              <div v-else class="divide-y">
                <button
                  v-for="doc in searchResults"
                  :key="doc.id"
                  class="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors flex items-start gap-2.5"
                  :class="{ 'bg-muted': currentDocument?.id === doc.id }"
                  @click="selectDocument(doc)"
                >
                  <div
                    class="h-8 w-8 rounded flex items-center justify-center text-white shrink-0 mt-0.5"
                    :class="documentTypeColors[doc.type]"
                  >
                    <FileText class="h-4 w-4" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium truncate">{{ doc.title }}</p>
                    <div class="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                      <Badge variant="outline" class="text-[10px] px-1 py-0">{{ documentTypeLabels[doc.type] }}</Badge>
                      <span>v{{ doc.version }}</span>
                      <span v-if="!doc.isPublic" class="flex items-center gap-0.5">
                        <Lock class="h-2.5 w-2.5" />
                      </span>
                    </div>
                    <div class="flex items-center gap-1.5 text-[11px] text-muted-foreground/70 mt-0.5">
                      <span>{{ entityTypeLabels[doc.entityType] || doc.entityType }}</span>
                      <span>·</span>
                      <span>{{ getDocAuthor(doc.updatedBy) }}</span>
                      <span>·</span>
                      <span>{{ formatDate(doc.updatedAt) }}</span>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <!-- Center: Document Content -->
          <div class="flex-1 flex flex-col min-w-0">
            <!-- Document Not Selected -->
            <div v-if="!currentDocument" class="flex-1 flex items-center justify-center">
              <div class="text-center">
                <FileText class="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p class="text-muted-foreground">Selecciona un documento para visualizar</p>
              </div>
            </div>

            <!-- Document Header -->
            <template v-else>
              <div class="px-6 py-3 border-b shrink-0">
                <div class="flex items-start gap-3">
                  <div
                    class="h-10 w-10 rounded flex items-center justify-center text-white shrink-0"
                    :class="documentTypeColors[currentDocument.type]"
                  >
                    <FileText class="h-5 w-5" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <h2 class="text-lg font-semibold flex items-center gap-2">
                      {{ currentDocument.title }}
                      <Badge v-if="!currentDocument.isPublic" variant="secondary" class="text-xs">
                        <Lock class="h-3 w-3 mr-1" />
                        Interno
                      </Badge>
                    </h2>
                    <div class="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground/70">
                      <span class="font-mono text-[11px]">{{ currentDocument.id }}</span>
                      <button
                        class="inline-flex items-center justify-center h-4 w-4 rounded hover:bg-muted transition-colors"
                        title="Copiar ID"
                        @click="copyDocumentId"
                      >
                        <Copy class="h-2.5 w-2.5" />
                      </button>
                    </div>
                    <div class="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                      <Badge variant="outline">{{ documentTypeLabels[currentDocument.type] }}</Badge>
                      <span class="flex items-center gap-1">
                        <Hash class="h-3 w-3" />
                        v{{ currentDocument.version }}
                      </span>
                      <span class="flex items-center gap-1.5">
                        <Avatar class="h-5 w-5">
                          <AvatarImage v-if="authorUser?.avatarUrl" :src="authorUser.avatarUrl" />
                          <AvatarFallback class="text-[8px]">{{ getInitials(authorUser?.displayName) }}</AvatarFallback>
                        </Avatar>
                        {{ authorName }}
                      </span>
                      <span class="flex items-center gap-1">
                        <Calendar class="h-3 w-3" />
                        {{ formatDate(currentDocument.createdAt) }}
                      </span>
                      <span v-if="currentDocument.viewCount" class="flex items-center gap-1">
                        <Eye class="h-3 w-3" />
                        {{ currentDocument.viewCount }}
                      </span>
                      <span v-if="currentDocument.version > 1" class="text-xs">
                        Actualizado por {{ updatedByName }} el {{ formatDate(currentDocument.updatedAt) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Reactions -->
              <div class="px-6 py-2 border-b shrink-0">
                <DocumentReactions
                  :document-id="currentDocument.id"
                  :reactions="currentDocument.reactions"
                  :actor-id="authStore.userId || ''"
                />
              </div>

              <!-- TTS Audio Player -->
              <div v-if="ttsShowPlayer" class="px-6 py-3 border-b shrink-0 bg-muted/30">
                <!-- Loading state -->
                <div v-if="ttsIsLoading" class="flex items-center gap-3">
                  <div class="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Loader2 class="h-5 w-5 animate-spin text-primary" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium">Generando audio...</p>
                    <p class="text-xs text-muted-foreground">El documento se está convirtiendo a voz. Esto puede tomar unos segundos.</p>
                    <!-- Skeleton progress bar -->
                    <div class="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div class="h-full w-1/3 rounded-full bg-primary/30 animate-pulse"></div>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" class="h-8 w-8 shrink-0" @click="closeTtsPlayer">
                    <X class="h-4 w-4" />
                  </Button>
                </div>

                <!-- Error state -->
                <div v-else-if="ttsError" class="flex items-center gap-3">
                  <div class="h-9 w-9 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
                    <Volume2 class="h-5 w-5 text-destructive" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-destructive">Error</p>
                    <p class="text-xs text-muted-foreground">{{ ttsError }}</p>
                  </div>
                  <Button variant="outline" size="sm" @click="startTts">Reintentar</Button>
                  <Button variant="ghost" size="icon" class="h-8 w-8 shrink-0" @click="closeTtsPlayer">
                    <X class="h-4 w-4" />
                  </Button>
                </div>

                <!-- Player controls -->
                <div v-else class="flex items-center gap-3">
                  <div class="flex items-center gap-1.5 shrink-0">
                    <Button
                      variant="default"
                      size="icon"
                      class="h-9 w-9 rounded-full"
                      @click="togglePlayPause"
                    >
                      <Pause v-if="ttsIsPlaying" class="h-4 w-4" />
                      <Play v-else class="h-4 w-4 ml-0.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      class="h-8 w-8"
                      @click="stopTts"
                    >
                      <Square class="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  <!-- Progress bar -->
                  <div class="flex-1 min-w-0 flex items-center gap-3">
                    <div
                      class="flex-1 h-1.5 rounded-full bg-muted cursor-pointer relative group"
                      @click="seekTts"
                    >
                      <div
                        class="h-full rounded-full bg-primary transition-all"
                        :style="{ width: `${ttsProgress}%` }"
                      ></div>
                      <div
                        class="absolute top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity"
                        :style="{ left: `${ttsProgress}%`, transform: 'translate(-50%, -50%)' }"
                      ></div>
                    </div>
                    <span class="text-xs text-muted-foreground tabular-nums shrink-0">
                      {{ ttsFormattedTime }}
                    </span>
                  </div>

                  <Button variant="ghost" size="icon" class="h-8 w-8 shrink-0" @click="closeTtsPlayer">
                    <X class="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <!-- Copilot Summary -->
              <div v-if="!contentTooLong || hasSummary" class="px-6 py-3 border-b shrink-0">
                <!-- No summary yet -->
                <div v-if="!hasSummary && !summaryIsGenerating" class="flex items-center justify-between">
                  <div class="flex items-center gap-2 text-sm text-muted-foreground">
                    <Sparkles class="h-4 w-4" />
                    <span>Resumen Copilot</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    @click="handleGenerateSummary"
                  >
                    <Sparkles class="h-3.5 w-3.5 mr-1.5" />
                    Generar resumen
                  </Button>
                </div>

                <!-- Generating -->
                <div v-else-if="summaryIsGenerating" class="flex items-center gap-3">
                  <div class="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Loader2 class="h-4 w-4 animate-spin text-primary" />
                  </div>
                  <div class="flex-1">
                    <p class="text-sm font-medium">Generando resumen...</p>
                    <p class="text-xs text-muted-foreground">El copilot está analizando el documento</p>
                  </div>
                </div>

                <!-- Summary exists -->
                <div v-else-if="hasSummary">
                  <!-- Header -->
                  <div class="flex items-center justify-between mb-1">
                    <button
                      class="flex items-center gap-2 text-sm font-medium hover:text-foreground transition-colors"
                      @click="summaryCollapsed = !summaryCollapsed"
                    >
                      <Sparkles class="h-4 w-4 text-primary" />
                      <span>Resumen Copilot</span>
                      <Badge v-if="summaryIsStale" variant="outline" class="text-[10px] px-1.5 py-0 text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950 dark:border-amber-700 dark:text-amber-400" title="Evalúe si requiere ser regenerado o ignore el mensaje">
                        <AlertTriangle class="h-2.5 w-2.5 mr-0.5" />
                        Contenido modificado
                      </Badge>
                      <component :is="summaryCollapsed ? ChevronDown : ChevronUp" class="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                    <div class="flex items-center gap-1.5">
                      <template v-if="summaryIsStale">
                        <Button
                          variant="ghost"
                          size="sm"
                          class="h-7 text-xs"
                          @click="handleConfirmSummary"
                          title="Marcar resumen como vigente"
                        >
                          <CheckCircle class="h-3.5 w-3.5 mr-1" />
                          Validar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          class="h-7 text-xs"
                          @click="handleGenerateSummary"
                          :disabled="summaryIsGenerating"
                        >
                          <RefreshCw class="h-3.5 w-3.5 mr-1" />
                          Regenerar
                        </Button>
                      </template>
                      <Button
                        v-else
                        variant="ghost"
                        size="sm"
                        class="h-7 text-xs"
                        @click="handleGenerateSummary"
                        :disabled="summaryIsGenerating"
                      >
                        <RefreshCw class="h-3.5 w-3.5 mr-1" />
                        Regenerar
                      </Button>
                    </div>
                  </div>
                  <!-- Summary content -->
                  <div v-if="!summaryCollapsed" class="mt-2 rounded-md px-4 py-3 max-h-72 overflow-y-auto">
                    <div class="prose prose-sm dark:prose-invert max-w-none text-sm">
                      <MdPreview
                        :model-value="currentDocument.copilotSummary!.content"
                        language="en-US"
                      />
                    </div>
                    <div class="mt-2 text-[11px] text-muted-foreground/60">
                      Generado en v{{ currentDocument.copilotSummary!.generatedByVersion }}
                      · {{ formatDate(currentDocument.copilotSummary!.generatedAt) }}
                    </div>
                  </div>
                </div>

                <!-- Error -->
                <div v-if="summaryError" class="mt-2 text-sm text-destructive">
                  {{ summaryError }}
                </div>
              </div>

              <!-- Content -->
              <div class="flex-1 overflow-auto px-6 py-4">
                <DocumentMarkdownView :content="currentDocument.content" />
              </div>
            </template>
          </div>

          <!-- Right: Comments (hidden on mobile) -->
          <div
            v-if="currentDocument && !isMobile"
            class="shrink-0 border-l transition-all duration-200"
            :class="commentsPanelCollapsed ? 'w-10' : 'w-[380px]'"
          >
            <!-- Collapsed state -->
            <div v-if="commentsPanelCollapsed" class="flex flex-col items-center py-2">
              <Button variant="ghost" size="icon" class="h-8 w-8" @click="commentsPanelCollapsed = false" title="Expandir comentarios">
                <PanelRightOpen class="h-4 w-4" />
              </Button>
            </div>
            <!-- Expanded state -->
            <div v-else class="h-full flex flex-col">
              <div class="flex items-center justify-between px-4 py-2 border-b shrink-0">
                <span class="text-sm font-medium">Comentarios</span>
                <Button variant="ghost" size="icon" class="h-8 w-8" @click="commentsPanelCollapsed = true" title="Contraer comentarios">
                  <PanelRightClose class="h-4 w-4" />
                </Button>
              </div>
              <div class="flex-1 overflow-auto px-4 py-4">
                <CommentsList
                  entity-type="document"
                  :entity-id="currentDocument.id"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile: Collapsible Search & Comments at bottom -->
        <template v-if="isMobile && currentDocument">
          <!-- Mobile Search -->
          <div class="border-t shrink-0">
            <button
              class="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium hover:bg-muted/50 transition-colors"
              @click="searchPanelCollapsed = !searchPanelCollapsed"
            >
              <span class="flex items-center gap-2">
                <Search class="h-4 w-4" />
                Buscar documentos
              </span>
              <component :is="searchPanelCollapsed ? ChevronDown : ChevronUp" class="h-4 w-4 text-muted-foreground" />
            </button>
            <div v-if="!searchPanelCollapsed" class="border-t px-4 py-3 space-y-2 max-h-64 overflow-y-auto">
              <div class="relative">
                <Search class="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  v-model="searchQuery"
                  placeholder="Buscar documentos..."
                  class="pl-8 h-8 text-sm"
                  @keydown.enter="doSearch"
                />
              </div>
              <Select v-model="entityFilter">
                <SelectTrigger class="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent class="z-[200]">
                  <SelectItem v-for="opt in entityTypeOptions" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" class="w-full h-8" @click="doSearch" :disabled="isSearching">
                <Loader2 v-if="isSearching" class="h-3.5 w-3.5 mr-1.5 animate-spin" />
                <Search v-else class="h-3.5 w-3.5 mr-1.5" />
                Buscar
              </Button>
              <div v-if="isSearching" class="flex items-center justify-center py-4">
                <Loader2 class="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
              <div v-else-if="hasSearched && searchResults.length === 0" class="py-4 text-center">
                <p class="text-sm text-muted-foreground">Sin resultados</p>
              </div>
              <div v-else-if="searchResults.length > 0" class="divide-y">
                <button
                  v-for="doc in searchResults"
                  :key="doc.id"
                  class="w-full text-left py-2 hover:bg-muted/50 transition-colors flex items-start gap-2"
                  :class="{ 'bg-muted': currentDocument?.id === doc.id }"
                  @click="selectDocument(doc); searchPanelCollapsed = true"
                >
                  <div class="h-7 w-7 rounded flex items-center justify-center text-white shrink-0" :class="documentTypeColors[doc.type]">
                    <FileText class="h-3.5 w-3.5" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium truncate">{{ doc.title }}</p>
                    <p class="text-[11px] text-muted-foreground">{{ entityTypeLabels[doc.entityType] || doc.entityType }} · {{ formatDate(doc.updatedAt) }}</p>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <!-- Mobile Comments -->
          <div class="border-t shrink-0">
            <button
              class="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium hover:bg-muted/50 transition-colors"
              @click="commentsPanelCollapsed = !commentsPanelCollapsed"
            >
              <span class="flex items-center gap-2">
                <FileText class="h-4 w-4" />
                Comentarios
              </span>
              <component :is="commentsPanelCollapsed ? ChevronDown : ChevronUp" class="h-4 w-4 text-muted-foreground" />
            </button>
            <div v-if="!commentsPanelCollapsed" class="border-t px-4 py-3 max-h-72 overflow-y-auto">
              <CommentsList
                entity-type="document"
                :entity-id="currentDocument.id"
              />
            </div>
          </div>
        </template>
      </div>
    </Transition>
  </Teleport>
</template>

<style>
.md-editor-preview-wrapper {
  padding: 0 !important;
}
.md-editor-preview {
  background: transparent !important;
}
</style>
